import { useState } from "react";
import { KeyRound, Trash2 } from "lucide-react";
import { useMutation } from "@tanstack/react-query";
import {
  useDataProvider,
  useEditController,
  useGetIdentity,
  useNotify,
  useRecordContext,
  useRedirect,
  useTranslate,
} from "ra-core";
import type { SubmitHandler } from "react-hook-form";
import { SimpleForm } from "@/components/admin/simple-form";
import { CancelButton } from "@/components/admin/cancel-button";
import { SaveButton } from "@/components/admin/form";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Confirm } from "@/components/admin/confirm";

import type { CrmDataProvider } from "../providers/types";
import type { Sale, SalesFormData } from "../types";
import { getSalesErrorNotification } from "./salesErrorNotification";
import { SalesInputs } from "./SalesInputs";
import { AccessLinkPanel } from "./AccessLinkPanel";

function EditToolbar() {
  return (
    <div className="flex justify-end gap-4">
      <CancelButton />
      <SaveButton />
    </div>
  );
}

export function SalesEdit() {
  const { record } = useEditController();

  const dataProvider = useDataProvider<CrmDataProvider>();
  const notify = useNotify();
  const redirect = useRedirect();
  const translate = useTranslate();

  const { mutate } = useMutation({
    mutationKey: ["signup"],
    mutationFn: async (data: SalesFormData) => {
      if (!record) {
        throw new Error(
          translate("resources.sales.edit.record_not_found", {
            _: "Record not found",
          }),
        );
      }
      return dataProvider.salesUpdate(record.id, data);
    },
    onSuccess: () => {
      redirect("/sales");
      notify("resources.sales.edit.success", {
        messageArgs: {
          _: "User updated successfully",
        },
      });
    },
    onError: (error) => {
      const { message, args } = getSalesErrorNotification(
        error,
        "resources.sales.edit.error",
      );
      notify(message, {
        type: "error",
        messageArgs: { ...args, _: "An error occurred. Please try again." },
      });
    },
  });

  const onSubmit: SubmitHandler<SalesFormData> = async (data) => {
    mutate(data);
  };

  return (
    <div className="max-w-lg w-full mx-auto mt-8">
      <Card>
        <CardContent>
          <SimpleForm
            toolbar={<EditToolbar />}
            onSubmit={onSubmit as SubmitHandler<any>}
            record={record}
          >
            <SaleEditTitle />
            <SalesInputs />
          </SimpleForm>
        </CardContent>
      </Card>
      {record ? <NewAccessLink sale={record as Sale} /> : null}
      {record ? <DeleteTeamMember sale={record as Sale} /> : null}
    </div>
  );
}

// Team members have no email delivery: an administrator generates a new
// one-time link here when the previous one expired or the password was lost.
const NewAccessLink = ({ sale }: { sale: Sale }) => {
  const dataProvider = useDataProvider<CrmDataProvider>();
  const notify = useNotify();
  const translate = useTranslate();
  const [link, setLink] = useState<string | null>(null);
  const { mutate, isPending } = useMutation({
    mutationFn: () => dataProvider.salesAccessLink(sale.email),
    onSuccess: setLink,
    onError: () => notify("ra.notification.http_error", { type: "error" }),
  });

  return (
    <Card className="mt-4">
      <CardContent>
        {link ? (
          <AccessLinkPanel
            name={sale.first_name}
            link={link}
            onDone={() => setLink(null)}
          />
        ) : (
          <Button
            variant="outline"
            className="w-full rounded-full"
            disabled={isPending}
            onClick={() => mutate()}
          >
            <KeyRound className="size-4" />
            {translate("resources.sales.edit.new_access_link")}
          </Button>
        )}
      </CardContent>
    </Card>
  );
};

// Deletes the person for real (account included). Their contacts, deals,
// tasks and notes move to the administrator doing it.
const DeleteTeamMember = ({ sale }: { sale: Sale }) => {
  const dataProvider = useDataProvider<CrmDataProvider>();
  const notify = useNotify();
  const redirect = useRedirect();
  const translate = useTranslate();
  const { identity } = useGetIdentity();
  const [open, setOpen] = useState(false);
  const { mutate, isPending } = useMutation({
    mutationFn: () => dataProvider.salesDelete(sale.id),
    onSuccess: () => {
      notify("resources.sales.delete.success", { type: "success" });
      redirect("/sales");
    },
    onError: () => notify("resources.sales.delete.error", { type: "error" }),
  });

  if (identity?.id === sale.id) return null;

  return (
    <Card className="mt-4">
      <CardContent>
        <Button
          variant="outline"
          className="w-full rounded-full text-destructive hover:text-destructive"
          onClick={() => setOpen(true)}
        >
          <Trash2 className="size-4" />
          {translate("resources.sales.delete.button")}
        </Button>
        <Confirm
          isOpen={open}
          loading={isPending}
          title="resources.sales.delete.title"
          titleTranslateOptions={{
            name: `${sale.first_name} ${sale.last_name}`.trim(),
          }}
          content="resources.sales.delete.description"
          confirm="resources.sales.delete.confirm"
          confirmColor="warning"
          onConfirm={() => mutate()}
          onClose={() => setOpen(false)}
        />
      </CardContent>
    </Card>
  );
};

const SaleEditTitle = () => {
  const record = useRecordContext<Sale>();
  const translate = useTranslate();
  if (!record) return null;
  return (
    <h2 className="text-lg font-semibold mb-4">
      {translate("resources.sales.edit.title", {
        name: `${record.first_name} ${record.last_name}`,
      })}
    </h2>
  );
};
